<template>
  <div
    class="two-for-one-hero flex flex-col-reverse lg:flex-row"
    :class="{ 'lg:p-20': !isTwoForOneCampaign, 'pb-20 lg:pb-0 lg:mt-7': isTwoForOneCampaign }"
    :style="{ '--campaign-color': campaignColor }"
  >
    <div
      :class="[
        { 'lg:py-10': isTwoForOneCampaign, 'lg:px-0': !isTwoForOneCampaign },
        `lg:flex-1 px-4 lg:rounded-l-lg lg:px-7 lg:flex lg:justify-center lg:items-center ${bgClass}`
      ]"
    >
      <div>
        <h1 class="ota-h1 font-extrabold mt-5 lg:mt-0" v-html="title" />
        <p class="ota-p18 mt-4 font-bold lg:mt-5" v-text="subTitle" />
        <div class="max-usps-w">
          <USPs :usps="tryNowUsps" />
        </div>
        <NewButton
          tag="nuxt-link"
          to="/men/signup"
          icon-right="new-chevron-right"
          :attribute="attribute"
        >
          {{ isTwoForOneCampaign ? $t('TwoForOneCampaign.TryForFree') : $t('TryItNow.StartFreeSubscription') }}
        </NewButton>
      </div>
    </div>
    <div class="lg:flex justify-center relative items-center">
      <template v-if="isTwoForOneCampaign">
        <div>
          <OImage
            v-bind="banner"
            img-class="w-full lg:rounded-r-lg two-for-one-image"
            :alt="$t('Accessibilty.AltTags.OnePlusOneCampaignImage')"
          />
          <p class="font-extrabold font-display product-image-text lg:hidden" v-text="$t(`SignUp.OnePage.Campaign.TwoForOne.ProductImageText`)" />
        </div>
      </template>
      <video
        v-else
        ref="video"
        type="video/mp4"
        muted
        autoplay
        loop
        playsinline
        disableremoteplayback
        :src="heroVideo"
        class="gif-video px-10 lg:px-0"
        @ended="onEnded"
      />
    </div>
  </div>
</template>

<script>
import { ActiveCampaign } from '@/enums/campaigns'
import { addLocalCurrency, convertSrc, getEuros, getImagePaths, isFeaturedBoxersCampaign } from '@/utils/general'
import { MenPrices } from '@/enums/products'
import { CountryCodes, Currencies, Locales } from '@/enums/general'
import USPs from '@/components/USPs'
import NewButton from '@/components/NewButton'
import OImage from '@/components/OImage'

export default {
  name: 'TwoForOneHero',
  components: {
    USPs,
    NewButton,
    OImage
  },
  props: {
    isTwoForOneCampaign: {
      type: Boolean,
      default: false
    },
    isLastChanceTwoForOneCampaign: {
      type: Boolean,
      default: false
    },
    locale: {
      type: String,
      default: Locales.nl
    },
    country: {
      type: String,
      default: CountryCodes.netherlands
    },
    bgClass: {
      type: String,
      default: ''
    },
    attribute: {
      type: Object,
      default: null
    }
  },
  computed: {
    title () {
      if (this.isTwoForOneCampaign) {
        if (this.isLastChanceTwoForOneCampaign) {
          return this.$t(`TryItNow.Campaigns.${ActiveCampaign.name}.LastChance.Title`, { ...this.variables })
        }

        return this.$t(`TryItNow.Campaigns.${ActiveCampaign.name}.Title`, { ...this.variables })
      }

      return this.$t('TryItNow.AlwaysThePerfectBoxershortBR', { ...this.variables })
    },
    subTitle () {
      if (this.isTwoForOneCampaign) {
        if (this.isLastChanceTwoForOneCampaign) {
          return this.$t(`TryItNow.Campaigns.${ActiveCampaign.name}.LastChance.SubTitle`, { ...this.variables })
        }

        return this.$t(`TryItNow.Campaigns.${ActiveCampaign.name}.SubTitle`, { ...this.variables })
      }

      return this.$t('TryItNow.MonthlyBoxershorts', { ...this.variables })
    },
    banner () {
      return getImagePaths({ path: `${ActiveCampaign.s3Path}/banner-m4r5-try-now`, s3: true })
    },
    variables () {
      const countryCode = `${this.locale}-${this.country}`

      return {
        country: this.$t(`Countries.${this.country}`),
        price: addLocalCurrency(MenPrices[this.country], countryCode, Currencies[this.country]).replace(/\s/g, ''),
        pricex2: addLocalCurrency(MenPrices[this.country] * 2, countryCode, Currencies[this.country]).replace(/\s/g, ''),
        loyaltyPoints: getEuros(this.country, 10)
      }
    },
    tryNowUsps () {
      const items = this.$t('TryItNow.USPS')

      if (!Array.isArray(items)) {
        return []
      }

      return items.map((_, index) => this.$t(`TryItNow.USPS[${index}]`, { ...this.variables }))
    },
    heroVideo () {
      if (this.isFeaturedBoxersCampaign) {
        return convertSrc('/images/frontend/try-now/halloween23/halloween_video_header.mp4', true)
      }

      if (this.isTwoForOneCampaign) {
        return convertSrc(`/images/frontend/try-now/video/video-header-${encodeURIComponent('1+1')}-campaign-${this.locale}.mp4`, true)
      }

      return convertSrc(`/images/frontend/try-now/video-header-${this.locale}.mp4`, true)
    },
    isFeaturedBoxersCampaign () {
      return isFeaturedBoxersCampaign(this.country)
    },
    campaignColor () {
      return ActiveCampaign.color
    }
  },
  methods: {
    onEnded () {
      if (this.$refs.video) {
        this.$refs.video.load()
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
.two-for-one-hero {
  video::-internal-media-controls-overlay-cast-button {
    display: none;
  }

  >>> .ota-usps-container {
    @apply py-4;
  }

  .product-image-text {
    @apply absolute bg-green-400 px-2 py-half text-white uppercase z-30;
    border-radius: .6rem;
    font-size: 3rem;
    right: 16%;
    top: 62%;
  }

  .oimage >>> .two-for-one-image {
    max-width: 48rem;
  }

  .flex-col-reverse {
    flex-direction: column-reverse;
  }

  .ota-h1.font-extrabold {
    @apply font-extrabold;
  }

  & > div {
    @apply overflow-visible;
  }

  .ota-h1 >>> span {
    color: var(--campaign-color);
  }

  .ota-h1 b {
    @apply text-turquoise-300;
  }

  .obutton {
    @apply px-4 !important;
  }

  .ota-usps {
    >>> .ota-usps-container {
      @apply bg-white px-0;

      svg {
        @apply fill-gray-600 w-4 h-4;
      }
    }
  }

  .what-you-get-usps {
    @apply px-0 !important;

    >>> .ota-usps-container {
      @apply p-0;

      svg {
        @apply w-4 h-4 fill-turquoise-300;
      }

      &-grid {
        @apply block;
        max-width: 32rem;
      }
    }
  }

  .max-usps-w {
    >>> .ota-usps-container-grid > .flex {
      .text-sm {
        font-size: 1.6rem;
      }
    }

    >>> .ota-usps-container-grid > .flex.mb-2 {
      @apply mb-4;
    }

    >>> .ota-usps-container-grid {
      @apply block;
    }
  }
}

@screen md {
  .gif-video {
    max-width: 36.5rem;
  }

  .product-image-text {
    right: 59%;
  }

  .boxer-max-width {
    width: 157rem;

    >>> img {
      max-width: 100% !important;
    }
  }

  .model-image-side {
    min-width: 40%;
  }
}

@screen lg {
  .ota-usps {
    @apply px-0;
  }

  .max-usps-w {
    max-width: 41rem;
  }
}
</style>
